import { useCallback, useEffect, useState } from "react";
import { TbAlertCircle, TbCreditCard, TbCheck, TbX, TbClock } from "react-icons/tb";
import { FiRefreshCw } from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
// Skeleton Loader
const TransactionCardSkeleton = () => (
  <div className="border border-gray-200 rounded-lg p-3 sm:p-5 animate-pulse">
    <div className="flex justify-between items-start mb-4">
      <div className="space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="h-3 w-40 bg-gray-200 rounded" />
      </div>
      <div className="h-6 w-20 bg-gray-200 rounded-full" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="h-4 w-24 bg-gray-200 rounded" />
      <div className="h-4 w-24 bg-gray-200 rounded" />
    </div>
  </div>
);
// Status Badge
const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { color: "bg-green-100 text-green-700", icon: TbCheck },
    initiated: { color: "bg-blue-100 text-blue-700", icon: TbClock },
    pending: { color: "bg-yellow-100 text-yellow-700", icon: TbClock },
    processing: { color: "bg-indigo-100 text-indigo-700", icon: FiRefreshCw },
    failed: { color: "bg-red-100 text-red-700", icon: TbX },
    cancelled: { color: "bg-gray-100 text-gray-700", icon: TbX },
    refunded: { color: "bg-purple-100 text-purple-700", icon: TbCreditCard },
  };
  const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon size={13} />
      {status}
    </span>
  );
};
// Empty State
const EmptyTransactions = () => (
  <div className="py-16 px-6 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
      <TbCreditCard size={40} className="text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-2">
      No Transactions Found
    </h3>
    <p className="text-sm text-gray-600">
      No payment transactions available for this order yet.
    </p>
  </div>
);
// Error State
const ErrorState = ({ error, onRetry }) => (
  <div className="py-16 px-6">
    <div className="max-w-md mx-auto">
      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <TbAlertCircle size={32} className="text-red-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
        Unable to Load Transactions
      </h3>
      <p className="text-sm text-gray-600 text-center mb-5">
        {error || "Something went wrong while fetching transactions."}
      </p>
      <button
        onClick={onRetry}
        className="w-full py-2.5 rounded-lg bg-[#772037] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Try Again
      </button>
    </div>
  </div>
);
// Transaction Card
const TransactionCard = ({ transaction }) => {
  const isCart = transaction.metadata?.isBulk === true;
  const formattedDate = new Date(transaction.createdAt).toLocaleDateString(
    "en-IN",
    { day: "numeric", month: "short", year: "numeric" }
  );
  const formattedTime = new Date(transaction.createdAt).toLocaleTimeString(
    "en-IN",
    { hour: "2-digit", minute: "2-digit" }
  );
  const buyerInfo = transaction.userId;
  return (
    <div className="border border-gray-200 rounded-lg p-3 sm:p-5 hover:border-[#772037]/30 transition-all duration-200 bg-white">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#772037]/10 flex items-center justify-center flex-shrink-0">
            <TbCreditCard className="text-[#772037]" size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">
              {transaction.transactionId}
            </p>
            <p className="text-xs text-gray-600">
              {transaction.description}
            </p>
          </div>
        </div>
        <StatusBadge status={transaction.status} />
      </div>
      {/* Buyer Information */}
      {buyerInfo && (
        <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-2">
            Buyer Information
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-gray-600">Name</p>
              <p className="font-semibold text-gray-800">
                {buyerInfo.firstName} {buyerInfo.lastName}
              </p>
            </div>
            {buyerInfo.email && (
              <div>
                <p className="text-gray-600">Email</p>
                <p className="font-mono text-gray-800 text-[10px] break-all">
                  {buyerInfo.email}
                </p>
              </div>
            )}
            {buyerInfo.phone && (
              <div>
                <p className="text-gray-600">Phone</p>
                <p className="font-semibold text-gray-800">
                  {buyerInfo.phone}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Details Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
            Amount
          </p>
          <p className="text-sm font-bold text-[#772037]">
            ₹{transaction.amount.toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
            Order Type
          </p>
          <p className="text-sm font-semibold text-gray-700">
            {transaction.orderType}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
            Date
          </p>
          <p className="text-sm text-gray-700">
            {formattedDate}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
            Time
          </p>
          <p className="text-sm text-gray-700">
            {formattedTime}
          </p>
        </div>
      </div>
      {/* Quotation Info */}
      <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-100">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          Quotation Details
        </p>
        {isCart ? (
          <div className="space-y-1">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Cart:</span>{" "}
              {transaction.metadata?.cartNumber || "N/A"}
            </p>
            <p className="text-xs text-gray-600">
              {transaction.quotationIds?.length || 0} quotations
            </p>
            {transaction.quotationUniqueNumbers &&
              transaction.quotationUniqueNumbers.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-[10px] font-semibold text-gray-600 mb-1">
                    Quotations:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {transaction.quotationUniqueNumbers.map((num, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-0.5 text-[10px] font-mono bg-[#772037]/10 text-[#772037] rounded"
                      >
                        {num}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        ) : (
          <p className="text-xs text-gray-700">
            <span className="font-semibold">Quotation #:</span>{" "}
            {transaction.quotationUniqueNumber}
          </p>
        )}
      </div>
      {/* Payment Gateway Details */}
      {transaction.paymentGateway && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-2">
            Payment Details
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-gray-600">Provider</p>
              <p className="font-semibold text-gray-800 capitalize">
                {transaction.paymentGateway.provider}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Order ID</p>
              <p className="font-mono text-gray-800 text-[10px] break-all">
                {transaction.paymentGateway.orderId}
              </p>
            </div>
            {transaction.paymentGateway.paymentId && (
              <div>
                <p className="text-gray-600">Payment ID</p>
                <p className="font-mono text-gray-800 text-[10px] break-all">
                  {transaction.paymentGateway.paymentId}
                </p>
              </div>
            )}
            {transaction.paymentGateway.method && (
              <div>
                <p className="text-gray-600">Method</p>
                <p className="font-semibold text-gray-800 capitalize">
                  {transaction.paymentGateway.method}
                </p>
              </div>
            )}
            {transaction.paymentGateway.bankName && (
              <div>
                <p className="text-gray-600">Bank</p>
                <p className="font-semibold text-gray-800">
                  {transaction.paymentGateway.bankName}
                </p>
              </div>
            )}
            {transaction.paymentGateway.walletName && (
              <div>
                <p className="text-gray-600">Wallet</p>
                <p className="font-semibold text-gray-800">
                  {transaction.paymentGateway.walletName}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Timestamps */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-[10px]">
        <div>
          <p className="text-gray-500">Initiated</p>
          <p className="text-gray-700 font-semibold">
            {new Date(transaction.initiatedAt).toLocaleDateString("en-IN")}
          </p>
        </div>
        {transaction.completedAt && (
          <div>
            <p className="text-gray-500">Completed</p>
            <p className="text-gray-700 font-semibold">
              {new Date(transaction.completedAt).toLocaleDateString("en-IN")}
            </p>
          </div>
        )}
        {transaction.failedAt && (
          <div>
            <p className="text-gray-500">Failed</p>
            <p className="text-gray-700 font-semibold">
              {new Date(transaction.failedAt).toLocaleDateString("en-IN")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
// Main Component
const TransactionsHistoryTab = ({ orderId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    orderType: "all",
  });
  const fetchTransactions = useCallback(async () => {
    if (!orderId) {
      setError("Order ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Real API Call
      const res = await axios.get(`/payment/get-order-transactions/${orderId}`);

      // Handle array or single object response
      const data = Array.isArray(res.data.data)
        ? res.data.data
        : res.data.data
          ? [res.data.data]
          : [];

      setTransactions(data);
      setSummary(res.data.summary);

    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to fetch transactions";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [orderId]);



  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);
  // Filter transactions
  const filteredTransactions = transactions.filter((txn) => {
    if (filters.status !== "all" && txn.status !== filters.status) {
      return false;
    }
    if (filters.orderType !== "all" && txn.orderType !== filters.orderType) {
      return false;
    }
    return true;
  });
  // Loading state
  if (loading) {
    return (
      <div className="space-y-5">
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <TransactionCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }
  // Error state
  if (error && transactions.length === 0) {
    return <ErrorState error={error} onRetry={fetchTransactions} />;
  }
  // Empty state
  if (transactions.length === 0) {
    return <EmptyTransactions />;
  }
  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 sm:p-4">
        <div className="flex items-start gap-3">
          <TbCreditCard className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Transaction History
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Payment transactions for this order. Total Amount Completed:{" "}
              <span className="font-bold">
                ₹{summary?.totalAmountCompleted?.toFixed(2) || "0.00"}
              </span>
              ({summary?.completedTransactions || 0} completed,{" "}
              {summary?.pendingTransactions || 0} pending,{" "}
              {summary?.failedTransactions || 0} failed)
            </p>
          </div>
        </div>
      </div>
      {/* Statistics Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-2 sm:p-4 rounded-lg border border-gray-200 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
              Total Transactions
            </p>
            <p className="text-2xl font-bold text-gray-800">
              {summary.totalTransactions}
            </p>
          </div>
          <div className="p-2 sm:p-4 rounded-lg border border-green-200 bg-green-50">
            <p className="text-xs font-semibold uppercase tracking-wider text-green-600 mb-2">
              Completed
            </p>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {summary.completedTransactions}
              </p>
              <p className="text-xs text-green-600/70 mt-1">
                ₹{summary.totalAmountCompleted.toFixed(2)}
              </p>
            </div>
          </div>
          <div className="p-2 sm:p-4 rounded-lg border border-yellow-200 bg-yellow-50">
            <p className="text-xs font-semibold uppercase tracking-wider text-yellow-600 mb-2">
              Pending
            </p>
            <p className="text-2xl font-bold text-yellow-600">
              {summary.pendingTransactions}
            </p>
          </div>
          <div className="p-2 sm:p-4 rounded-lg border border-red-200 bg-red-50">
            <p className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">
              Failed
            </p>
            <p className="text-2xl font-bold text-red-600">
              {summary.failedTransactions}
            </p>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
            Status:
          </label>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value })
            }
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 focus:ring-2 focus:ring-[#772037]/20 outline-none transition"
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="initiated">Initiated</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
            Order Type:
          </label>
          <select
            value={filters.orderType}
            onChange={(e) =>
              setFilters({ ...filters, orderType: e.target.value })
            }
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 focus:ring-2 focus:ring-[#772037]/20 outline-none transition"
          >
            <option value="all">All Types</option>
            <option value="Sample">Sample</option>
            <option value="Full_Order">Full Order</option>
          </select>
        </div>
        <button
          onClick={fetchTransactions}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:border-[#772037] transition text-sm font-semibold"
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>
      {/* Transactions List */}
      <div className="space-y-4">
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map((transaction) => (
            <TransactionCard key={transaction._id} transaction={transaction} />
          ))
        ) : (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-600">
              No transactions match the selected filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
export default TransactionsHistoryTab;